import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../services/logger.service';
import { Deal, DealStage } from '../types/deal';
import { dealAPI } from '../lib/deal-api';
import { clientAPI } from '../lib/client-api';
import { authService } from '../lib/auth';
import apiClient from '@/services/api-client';

interface NewDealModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDealCreated: (deal: Deal) => void;
  prefill?: {
    listingId?: string;
    propertyTitle?: string;
    propertyAddress?: string;
    listingPhotoUrl?: string;
  } | null;
}

interface ListingOption {
  id: string;
  title: string;
  location: string;
  listingId?: string;
  photoUrl?: string;
}

interface ClientOption {
  id: string;
  name: string;
  email: string;
}

export function NewDealModal({ open, onOpenChange, onDealCreated, prefill }: NewDealModalProps) {
  const [formData, setFormData] = useState({
    offerAmount: '',
    commissionPercent: '',
    type: 'sale' as 'sale' | 'rental',
    stage: 'lead' as DealStage,
    notes: '',
  });
  const [selectedListingId, setSelectedListingId] = useState<string>('');
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [listings, setListings] = useState<ListingOption[]>([]);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch agent's listings and clients when modal opens
  useEffect(() => {
    if (!open) return;
    setIsLoadingData(true);
    Promise.all([
      apiClient.get<any>('/my-listings?pageSize=100').then((r: any) => r?.data ?? r).catch(() => ({ listings: [] })),
      clientAPI.getClients().catch(() => []),
    ]).then(([listingsData, clientsData]) => {
      const mappedListings: ListingOption[] = (listingsData.listings || []).map((l: any) => ({
        id: l.somethingPropertyId || l.id || String(l.listingId || ''),
        title: l.title || l.marketingHeading || 'Untitled',
        location: typeof l.location === 'string' ? l.location : l.location?.address || l.address || '',
        listingId: l.somethingPropertyId || l.id || String(l.listingId || ''),
        photoUrl: l.images?.[0] || undefined,
      }));
      setListings(mappedListings);

      const mappedClients: ClientOption[] = (clientsData || []).map((c: any) => ({
        id: c.id || c.clientId || '',
        name: c.name || `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email || 'Unknown',
        email: c.email || '',
      }));
      setClients(mappedClients);

      // Pre-select listing if prefill has listingId
      if (prefill?.listingId) {
        const match = mappedListings.find(l => l.listingId === prefill.listingId || l.id === prefill.listingId);
        if (match) setSelectedListingId(match.id);
        else if (prefill.propertyTitle) {
          // Prefill from listing page — not in my-listings, add as manual entry
          setListings(prev => [
            { id: '__prefill__', title: prefill.propertyTitle!, location: prefill.propertyAddress || '', listingId: prefill.listingId, photoUrl: prefill.listingPhotoUrl },
            ...prev,
          ]);
          setSelectedListingId('__prefill__');
        }
      }
    }).finally(() => setIsLoadingData(false));
  }, [open]);

  const selectedListing = listings.find(l => l.id === selectedListingId);
  const selectedClient = clients.find(c => c.id === selectedClientId);

  const handleSubmit = async () => {
    if (!selectedListingId || !selectedClientId || !formData.offerAmount) {
      toast.error('Please select a property, a client, and enter an offer amount');
      return;
    }

    setIsSubmitting(true);
    try {
      const currentUser = authService.getCurrentUser();
      const agentName = currentUser?.name || 'Unknown Agent';

      const newDeal = await dealAPI.createDeal({
        propertyId: selectedListing?.listingId || selectedListingId,
        propertyTitle: selectedListing?.title || '',
        propertyAddress: selectedListing?.location || '',
        clientName: selectedClient?.name || '',
        clientId: selectedClientId,
        agentName,
        agentId: currentUser?.id || undefined,
        type: formData.type,
        dealType: formData.type,
        stage: formData.stage,
        offerAmount: Number(formData.offerAmount),
        salePrice: Number(formData.offerAmount),      // backend field name
        commissionRate: formData.commissionPercent ? Number(formData.commissionPercent) : 0, // backend field name
        probability: formData.stage === 'lead' ? 20 : 50,
        notes: formData.notes || undefined,
        ...(selectedListing?.listingId && { listingId: selectedListing.listingId }),
        ...(selectedListing?.photoUrl && { listingPhotoUrl: selectedListing.photoUrl }),
      } as any);

      onDealCreated(newDeal);
      toast.success('Deal created successfully!');

      // Reset
      setFormData({ offerAmount: '', commissionPercent: '', type: 'sale', stage: 'lead', notes: '' });
      setSelectedListingId('');
      setSelectedClientId('');
      onOpenChange(false);
    } catch (error) {
      logger.error('Error creating deal', error as Error, undefined, 'NewDealModal');
      toast.error('Failed to create deal. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Deal</DialogTitle>
          <DialogDescription>
            Add a new deal to your pipeline
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Property Information */}
          <div className="space-y-4">
            <h4 className="font-semibold">Property Information</h4>

            <div className="space-y-2">
              <Label htmlFor="deal-listing-select">
                Select Listing <span className="text-destructive">*</span>
              </Label>
              {isLoadingData ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading your listings...
                </div>
              ) : (
                <Select value={selectedListingId} onValueChange={setSelectedListingId}>
                  <SelectTrigger id="deal-listing-select">
                    <SelectValue placeholder="Choose a listing..." />
                  </SelectTrigger>
                  <SelectContent>
                    {listings.length === 0 ? (
                      <SelectItem value="__none__" disabled>No listings found</SelectItem>
                    ) : (
                      listings.map(l => (
                        <SelectItem key={l.id} value={l.id}>
                          <span className="font-medium">{l.title}</span>
                          {l.location && <span className="text-muted-foreground ml-2 text-xs">{l.location}</span>}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              )}
              {selectedListing && (
                <p className="text-xs text-muted-foreground">{selectedListing.location}</p>
              )}
            </div>
          </div>

          {/* Client Information */}
          <div className="space-y-4">
            <h4 className="font-semibold">Client Information</h4>

            <div className="space-y-2">
              <Label>
                Select Client <span className="text-destructive">*</span>
              </Label>
              {isLoadingData ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading your clients...
                </div>
              ) : (
                <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a client..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.length === 0 ? (
                      <SelectItem value="__none__" disabled>No clients found</SelectItem>
                    ) : (
                      clients.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          <span className="font-medium">{c.name}</span>
                          {c.email && <span className="text-muted-foreground ml-2 text-xs">{c.email}</span>}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          {/* Deal Details */}
          <div className="space-y-4">
            <h4 className="font-semibold">Deal Details</h4>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="type">
                  Deal Type <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.type}
                  onValueChange={(value: 'sale' | 'rental') => setFormData({ ...formData, type: value })}
                >
                  <SelectTrigger id="type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sale">Sale</SelectItem>
                    <SelectItem value="rental">Rental</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="stage">
                  Deal Stage <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.stage}
                  onValueChange={(value: DealStage) => setFormData({ ...formData, stage: value })}
                >
                  <SelectTrigger id="stage">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lead">Lead</SelectItem>
                    <SelectItem value="viewing-scheduled">Viewing Scheduled</SelectItem>
                    <SelectItem value="offer-made">Offer Made</SelectItem>
                    <SelectItem value="offer-accepted">Offer Accepted</SelectItem>
                    <SelectItem value="financing">Financing</SelectItem>
                    <SelectItem value="inspection">Inspection</SelectItem>
                    <SelectItem value="closing">Closing</SelectItem>
                    <SelectItem value="closed">Closed (Sold)</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="offerAmount">
                {formData.type === 'sale' ? 'Offer Amount' : 'Monthly Rental'} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="offerAmount"
                type="number"
                value={formData.offerAmount}
                onChange={(e) => setFormData({ ...formData, offerAmount: e.target.value })}
                placeholder="R 0"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="commissionPercent">Commission % (optional)</Label>
              <Input
                id="commissionPercent"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={formData.commissionPercent}
                onChange={(e) => setFormData({ ...formData, commissionPercent: e.target.value })}
                placeholder="e.g. 7.5"
              />
              {formData.commissionPercent && formData.offerAmount && (
                <p className="text-xs text-muted-foreground">
                  Commission: R {Math.round(Number(formData.offerAmount) * (Number(formData.commissionPercent) / 100)).toLocaleString()}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes about this deal..."
                rows={3}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <Button
              data-testid="new-deal-cancel-button"
              variant="outline"
              className="flex-1"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              data-testid="new-deal-create-button"
              className="flex-1"
              onClick={handleSubmit}
              disabled={isSubmitting || isLoadingData}
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Deal
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}